import { DayOfWeek } from '../models';
import { MinutesRange } from './MinutesRange';
export const MINUTES_IN_HOUR = 60;
export const HOURS_IN_DAY = 24;
export const convertDateRangeToMinutesRange = date => {
  return new MinutesRange({
    start: date.start.getTime(),
    end: date.end.getTime()
  });
};
const convertSlotToRange = slot => new MinutesRange({
  start: slot.start.hours * MINUTES_IN_HOUR + slot.start.minutes,
  end: slot.end.hours * MINUTES_IN_HOUR + slot.end.minutes
});
const convertRangeToSlot = range => ({
  start: {
    hours: Math.floor(range.start / MINUTES_IN_HOUR),
    minutes: range.start % MINUTES_IN_HOUR
  },
  end: {
    hours: Math.floor(range.end / MINUTES_IN_HOUR),
    minutes: range.end % MINUTES_IN_HOUR
  }
});

/**
 *  group availability slots by their respective base slots (if any) and add invalid slots to
 *  the proper list:
 */
const groupAndValidateSlots = (baseSlots, slots) => {
  const baseToSlotsMap = new Map(baseSlots.map(baseSlot => [baseSlot, []]));
  const outOfRangeSlotsIndices = [];
  const outOfLowerBoundSlotIndices = [];
  const outOfUpperBoundSlotIndices = [];
  const baseEdges = {
    start: baseSlots[0].start,
    end: baseSlots[baseSlots.length - 1].end
  };
  slots.forEach((slot, slotIndex) => {
    let baseSlotFound = false;
    for (const baseSlot of baseSlots) {
      if (slot.start >= baseSlot.start && slot.end <= baseSlot.end) {
        baseSlotFound = true;
        baseToSlotsMap.set(baseSlot, [...baseToSlotsMap.get(baseSlot), slot]);
        break;
      }
    }

    // if a containing base slot was not found for the slot, add it to the invalid slots list:
    if (!baseSlotFound) {
      outOfRangeSlotsIndices.push(slotIndex);
      if (slot.start < baseEdges.start) {
        outOfLowerBoundSlotIndices.push(slotIndex);
      }
      if (slot.end > baseEdges.end) {
        outOfUpperBoundSlotIndices.push(slotIndex);
      }
    }
  });
  return {
    baseToSlotsMap,
    outOfRangeSlotsIndices,
    outOfLowerBoundSlotIndices,
    outOfUpperBoundSlotIndices
  };
};

/**
 * Analyzes availability slots against base availability to determine slot validity.
 *
 * @param {AvailabilitySlot[]} baseAvailabilitySlots - The base availability slots to compare against.
 * @param {AvailabilitySlot[]} availabilitySlots - The availability slots to analyze.
 * @returns {BaseAvailabilitySlotsAnalysisResult} - The result of the slots analysis.
 */
export const analyzeAvailabilitySlotsAgainstBaseAvailability = (baseAvailabilitySlots, availabilitySlots) => {
  const result = {
    areSlotsAvailable: false,
    outOfRangeSlotsIndices: [],
    outOfLowerBoundSlotIndices: [],
    outOfUpperBoundSlotIndices: []
  };
  const allSlotsIndices = availabilitySlots.map((_, index) => index);
  const everythingInvalid = {
    areSlotsAvailable: false,
    outOfRangeSlotsIndices: allSlotsIndices,
    outOfLowerBoundSlotIndices: allSlotsIndices,
    outOfUpperBoundSlotIndices: allSlotsIndices
  };

  // if we have no base slots, we cannot add any slots and all slots are actually out of range:
  if (baseAvailabilitySlots.length === 0) {
    return everythingInvalid;
  }
  const baseSlots = mergeAvailabilitySlots(baseAvailabilitySlots).map(convertSlotToRange);

  // if we have no slots to compare, slots can be added and none is invalid:
  if (availabilitySlots.length === 0) {
    return {
      ...result,
      areSlotsAvailable: true
    };
  }

  // group availability slots by their respective base slots (if any) and add invalid slots to
  // the proper list:
  const slots = availabilitySlots.map(convertSlotToRange);
  const {
    baseToSlotsMap,
    outOfRangeSlotsIndices,
    outOfLowerBoundSlotIndices,
    outOfUpperBoundSlotIndices
  } = groupAndValidateSlots(baseSlots, slots);
  result.outOfRangeSlotsIndices = outOfRangeSlotsIndices;
  result.outOfLowerBoundSlotIndices = outOfLowerBoundSlotIndices;
  result.outOfUpperBoundSlotIndices = outOfUpperBoundSlotIndices;

  // check whether in any base slots there is any free time. if so, we can flag that
  // there are slots available and abort:
  for (const [baseSlot, baseSlotSlots] of baseToSlotsMap.entries()) {
    // if we have a base slot without any matching slots, we have slots available, so we can flag and abort:
    if (baseSlotSlots.length === 0) {
      result.areSlotsAvailable = true;
      break;
    }

    // merge any overlapping (or back-to-back) slots:
    const mergedSlots = mergeOverlappingMinutesRanges(baseSlotSlots);
    for (const baseSlotSlot of mergedSlots) {
      const isThereFreeTimeInBaseSlot = baseSlotSlot.start > baseSlot.start || baseSlotSlot.end < baseSlot.end;
      if (isThereFreeTimeInBaseSlot) {
        result.areSlotsAvailable = true;
        break;
      }
    }
  }
  return result;
};
export const mergeWeeklyAvailabilities = weeklyAvailabilities => {
  const daysOfWeek = Object.values(DayOfWeek);
  const mergedWeeklyAvailability = weeklyAvailabilities.reduce((merged, weeklyAvailability) => {
    daysOfWeek.forEach(dayOfWeek => {
      var _merged$dayOfWeek;
      merged[dayOfWeek] = {
        dayOfWeek,
        availabilities: [...(((_merged$dayOfWeek = merged[dayOfWeek]) == null ? void 0 : _merged$dayOfWeek.availabilities) ?? []), ...weeklyAvailability[dayOfWeek].availabilities]
      };
    });
    return merged;
  }, {});
  daysOfWeek.forEach(dayOfWeek => {
    mergedWeeklyAvailability[dayOfWeek].availabilities = mergeAvailabilitySlots(mergedWeeklyAvailability[dayOfWeek].availabilities);
  });
  return mergedWeeklyAvailability;
};
export const mergeAvailabilitySlots = slots => {
  const ranges = slots.map(convertSlotToRange);
  const merged = mergeOverlappingMinutesRanges(ranges);
  return merged.map(convertRangeToSlot);
};
export function mergeOverlappingMinutesRanges(range) {
  const sortedByStart = [...range].sort((a, b) => a.start > b.start ? 1 : -1);
  const result = [];
  let currentDate = sortedByStart.shift();
  if (!currentDate) {
    return result;
  }
  while (sortedByStart.length > -1) {
    const nextDate = sortedByStart.shift();
    if (!nextDate) {
      result.push(currentDate);
      break;
    }
    if (currentDate.end < nextDate.start) {
      result.push(currentDate);
      currentDate = nextDate;
    } else {
      currentDate.end = currentDate.end > nextDate.end ? currentDate.end : nextDate.end;
    }
  }
  return result;
}
export const getClientTimezoneOffsetHours = () => {
  // Date.getTimezoneOffset return the offset from LOCAL to UTC in minutes (e.g. local GMT+3 will
  // return -180), so we need to multiply it by -1:
  return -1 * new Date().getTimezoneOffset() / MINUTES_IN_HOUR;
};
//# sourceMappingURL=timeUtils.js.map